import React, { useEffect, createContext, useState, useContext, ReactNode } from "react";
import moment from "moment-timezone";
import { cstMoment } from "@common/utils/date.utils";

interface LiveVariantState {
    version: string;
    property_play_date: string;
    show_archive_select_modal: boolean;
    show_date_picker_tooltip: boolean;
    same_with_current_date: boolean;
    shortLink: string | null;
    playablePropertyId: number | null;
}

interface LiveVariantContextType {
    liveVariantState: LiveVariantState;
    setLiveVariantState: React.Dispatch<React.SetStateAction<LiveVariantState>>;
}

const LiveVariantContext = createContext<LiveVariantContextType | undefined>(undefined);

const currentDate = cstMoment().format("YYYY-MM-DD");

interface LiveVariantProviderProps {
    children: ReactNode;
}

export const LiveVariantProvider = ({ children }: LiveVariantProviderProps) => {
    const dateSegment = window.location.pathname.split("/")[2];
    const currentPropertyDate = moment(dateSegment, "YYMMDD").format("YYYY-MM-DD");

    const initialLiveVariantState: LiveVariantState = {
        version: "",
        property_play_date: currentDate,
        show_archive_select_modal: false,
        show_date_picker_tooltip: false,
        same_with_current_date: currentDate === currentPropertyDate, // Initialize via actual comparison and not false by default
        shortLink: null,
        playablePropertyId: null,
    };

    const [liveVariantState, setLiveVariantState] = useState<LiveVariantState>(initialLiveVariantState);

    useEffect(() => {
        const dateToday = moment(currentDate).startOf("day");
        const propertyDate = moment(liveVariantState.property_play_date).startOf("day");

        setLiveVariantState({
            ...liveVariantState,
            same_with_current_date: propertyDate.isSame(dateToday, "day"),
        });
    }, [liveVariantState.property_play_date]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <LiveVariantContext.Provider
            value={{
                liveVariantState,
                setLiveVariantState,
            }}
        >
            {children}
        </LiveVariantContext.Provider>
    );
};

export const useLiveVariant = (): LiveVariantContextType => {
    const context = useContext(LiveVariantContext);
    if (context === undefined) {
        throw new Error("useLiveVariant must be used within a LiveVariantProvider");
    }
    return context;
};
